import { jsx, jsxs } from 'react/jsx-runtime';
import { composeEventHandlers } from '@radix-ui/primitive';
import { useComposedRefs } from '@radix-ui/react-compose-refs';
import { assignInlineVars } from '@vanilla-extract/dynamic';
import classNames from 'classnames';
import { forwardRef, useMemo } from 'react';
import usePopperPresence from '../../../../hooks/usePopperPresence.mjs';
import { usePopperZIndexStyle } from '../../../../hooks/usePopperZIndexStyle.mjs';
import { usePreventTransitionInteraction } from '../../../../hooks/usePreventTransitionInteraction.mjs';
import { getComputedVerticalCollisionPadding } from '../../../../utils/collision-padding.mjs';
import { ScrollArea } from '../../../scroll-area/ScrollArea.mjs';
import { DROPDOWN_CONTENT_DEFAULT_COLLISION_PADDING, DROPDOWN_CONTENT_DEFAULT_SIDE_OFFSET } from '../../constants/style.mjs';
import { useDropdownContext } from '../../contexts/context.mjs';
import { DropdownMenuPortal, DropdownMenuContent } from '../../patched-dropdown/Dropdown.mjs';
import { dropdownContent } from '../../shared/styles/content.vanilla.mjs';
import { dropdownItemList } from '../../shared/styles/itemList.vanilla.mjs';
import { dropdownContentExternalMaxHeightSize, dropdownContentVerticalCollisionPaddingSize, dropdownContentFooterHeight, dropdownContentAffixHeight } from '../../shared/styles/variable.vanilla.mjs';
import { dropdownContentVariants, dropdownContentScrollAreaViewport } from './content.vanilla.mjs';

const DropdownContent = forwardRef(
  ({
    children,
    className: classNameFromProps,
    align = "start",
    affix,
    collisionPadding = DROPDOWN_CONTENT_DEFAULT_COLLISION_PADDING,
    sideOffset = DROPDOWN_CONTENT_DEFAULT_SIDE_OFFSET,
    footer,
    fitToTrigger,
    prefersReducedMotion,
    maxHeight,
    portalContainer,
    style: styleFromProps,
    zIndex,
    onKeyDownCapture,
    ...props
  }, forwardedRef) => {
    const { open, contentAffixElement, contentFooterElement, onClose } = useDropdownContext("Content");
    const preventTransitionInteraction = usePreventTransitionInteraction({
      open
    });
    const popperZIndexStyle = usePopperZIndexStyle(open, zIndex);
    const { ref: presenceRef } = usePopperPresence({
      open,
      onPresenceChange: (present) => {
        if (!present) {
          onClose?.();
        }
      }
    });
    const composedRef = useComposedRefs(forwardedRef, presenceRef);
    const style = useMemo(() => {
      const computedVerticalCollisionPadding = getComputedVerticalCollisionPadding({
        collisionPadding,
        fallbackCollisionPadding: DROPDOWN_CONTENT_DEFAULT_COLLISION_PADDING
      });
      const externalMaxHeight = typeof maxHeight === "number" ? `${maxHeight}px` : maxHeight;
      const contentAffixHeight = contentAffixElement?.clientHeight ?? 0;
      const contentFooterHeight = contentFooterElement?.clientHeight ?? 0;
      return Object.assign(
        {},
        popperZIndexStyle,
        preventTransitionInteraction.style,
        styleFromProps,
        assignInlineVars({
          [dropdownContentAffixHeight]: `${contentAffixHeight}px`,
          [dropdownContentFooterHeight]: `${contentFooterHeight}px`,
          [dropdownContentVerticalCollisionPaddingSize]: `${computedVerticalCollisionPadding}px`,
          [dropdownContentExternalMaxHeightSize]: externalMaxHeight ?? null
        })
      );
    }, [
      collisionPadding,
      contentAffixElement?.clientHeight,
      contentFooterElement?.clientHeight,
      maxHeight,
      popperZIndexStyle,
      preventTransitionInteraction.style,
      styleFromProps
    ]);
    const className = classNames(
      dropdownContent,
      dropdownContentVariants({ fitToTrigger, prefersReducedMotion }),
      classNameFromProps
    );
    const handleKeyDownCapture = composeEventHandlers(
      onKeyDownCapture,
      preventTransitionInteraction.onKeyDownCapture,
      { checkForDefaultPrevented: false }
    );
    return /* @__PURE__ */ jsx(DropdownMenuPortal, { container: portalContainer, children: /* @__PURE__ */ jsxs(
      DropdownMenuContent,
      {
        "data-scope": "dropdown",
        "data-part": "content",
        align,
        className,
        collisionPadding,
        sideOffset,
        style,
        onKeyDownCapture: handleKeyDownCapture,
        ref: composedRef,
        ...props,
        children: [
          affix,
          /* @__PURE__ */ jsx(ScrollArea, { "data-scope": "dropdown", "data-part": "content-scroll-area", children: /* @__PURE__ */ jsx(
            ScrollArea.Viewport,
            {
              "data-scope": "dropdown",
              "data-part": "content-scroll-area-viewport",
              className: dropdownContentScrollAreaViewport,
              children: /* @__PURE__ */ jsx(
                "div",
                {
                  "data-scope": "dropdown",
                  "data-part": "content-item-list",
                  className: dropdownItemList,
                  children
                }
              )
            }
          ) }),
          footer
        ]
      }
    ) });
  }
);

export { DropdownContent };
//# sourceMappingURL=Content.mjs.map
